export enum CommandStatus {
  CommandOk = 'ok',
  CommandFail = 'fail',
}

export type PlayerErrorType = 'video_error' | 'drm_error';
export type PlayerState =
  | 'notReady'
  | 'opening'
  | 'ready'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'DisplayingVideo'
  | 'endOfStream'
  | 'stopped';

export interface VideoMetrics {
  bandwidth: number;
  bitrates: number[];
  bytesDownloaded: number;
  codecs: string;
  currentBitrate: number;
  currentBufferLength: number;
  currentState: PlayerState;
  droppedFrames: number;
  duration: number;
  elapsed: number;
  encodedFramerate: number;
  frameworkVersion: string;
  live: boolean;
  maxBufferLength: number;
  naturalHeight: number;
  naturalWidth: number;
  renderedFramerate: number;
  sampleRate: number;
  url: string;
}

export interface WebMafResponse {
  command: string;
  status: CommandStatus;
}

export interface LoadResponse extends WebMafResponse {
  message: string;
}

export interface PlayerStatusChangeResponse extends WebMafResponse {
  playerState: PlayerState;
}

export interface PlayerErrorResponse extends WebMafResponse {
  error: PlayerErrorType;
  error_code: number;
  error_code_hexa: string;
  error_info: string;
}

export interface PlayerStreamingErrorResponse extends WebMafResponse {
  error: string;
  status_code: string;
}

export interface PlayerSeekWindowResponse extends WebMafResponse {
  seekable: boolean;
  startTime: number;
  endTime: number;
}

export enum PlayerErrorHexCode {
  //  invalid parameter specified
  PLAYER_ERROR_INVALID_PARAMS = '0x806A0001',

  // the requested operation has failed
  PLAYER_ERROR_OPERATION_FAILED = '0x806A0002',

  // memory allocation has failed
  PLAYER_ERROR_NO_MEMORY = '0x806A0003',

  // requested operation is not supported
  PLAYER_ERROR_NOT_SUPPORTED = '0x806A0004',

  // event notification that the file is non-interleaved
  PLAYER_ERROR_WAR_FILE_NONINTERLEAVED = '0x806A00A0',

  // event notification that the stream is looping back
  PLAYER_ERROR_WAR_LOOPING_BACK = '0x806A00A1',

  // event notification that the jump was completed
  PLAYER_ERROR_WAR_JUMP_COMPLETE = '0x806A00A3',

  // event notification of stream encrypted using Marlin
  PLAYER_ERROR_INFO_MARLIN_ENCRY = '0x806A00B0',

  // event notification of stream encrypted using PlayReady
  PLAYER_ERROR_INFO_PLAYREADY_ENCRY = '0x806A00B4',

  // event notification of stream encrypted using AES
  PLAYER_ERROR_INFO_AES_ENCRY = '0x806A00B5',

  // event notification of stream encrypted using other encryption technology
  PLAYER_ERROR_INFO_OTHER_ENCRY = '0x806A00BF',
}

export interface PlayerMessageResponse extends WebMafResponse {
  msg_code: number;
  msg_code_hexa: PlayerErrorHexCode;
  msg_info: string;
}

export interface TimedTextPosition {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export interface TimedEventDetail {
  language?: string;
  fontsize?: string;
  position?: TimedTextPosition;
  scheme?: string;
  timescale?: number;
  eventDuration?: number;
  id?: number;
  value?: string;
}

export enum TimedEventType {
  CEA_608 = 'cea-608',
  CEA_708 = 'cea-708',
  TEXT = 'text',
  EMSG = 'emsg',
  DATA = 'data',
  WEBVTT = 'webvtt',
}

export interface PlayerTimedEventResponse extends WebMafResponse {
  data: string;
  datatype: TimedEventType;
  timestamp: string;
  details: TimedEventDetail;
}

export interface DASHTrackInfo {
  AdaptationSet_id?: string;
  Representation_id_first?: string;
  Representation_id_last?: string;
}

export interface HLSTrackInfo {
  GROUP_ID?: string;
  NAME?: string;
}

export interface WebMafTrackInfo extends DASHTrackInfo, HLSTrackInfo {
  LANGUAGE: string;
}

export interface AudioTrackInfo extends WebMafTrackInfo, DASHTrackInfo, HLSTrackInfo {
  CHANNELS: number;
}

export interface SubtitleTrackInfo extends WebMafTrackInfo, DASHTrackInfo, HLSTrackInfo {}

export interface AudioTracksResponse extends WebMafResponse {
  audioTracks: string;
  audioNumChannels: string;
  currentAudioTrack: string;
  allAudioInfo: AudioTrackInfo[];
}

export interface SubtitleTracksResponse extends WebMafResponse {
  subtitleTracks: string;
  currentSubtitleTrack: string;
  allSubtitleInfo: SubtitleTrackInfo[];
}
